/**
 * FeatureDiscoveryCard Component
 * Displays feature discovery tracking metrics
 *
 * Story 5.3: Feature Discovery Tracking
 */

import React from 'react';
import { clsx } from 'clsx';
import { Sparkles, Target, MessageSquare, ClipboardCheck, CheckCircle } from 'lucide-react';
import { Card } from '../../common';
import type { FeatureDiscoveryData, FeatureDiscoveryItem } from '../../../services/reportApi';

export interface FeatureDiscoveryCardProps {
  /** Feature discovery data */
  data: FeatureDiscoveryData;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const DiscoverySkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-stone-200 rounded w-40" />
      <div className="h-6 bg-stone-200 rounded w-24" />
    </div>
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="w-10 h-10 bg-stone-200 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-stone-200 rounded w-24 mb-2" />
            <div className="h-2 bg-stone-100 rounded-full" />
          </div>
          <div className="h-6 bg-stone-200 rounded w-16" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Get icon for feature
 */
function getFeatureIcon(feature: string): React.ComponentType<{ className?: string }> {
  switch (feature) {
    case 'okr_creation':
      return Target;
    case 'key_results':
      return CheckCircle;
    case 'check_ins':
      return ClipboardCheck;
    case 'comments':
      return MessageSquare;
    default:
      return Sparkles;
  }
}

/**
 * Feature row component
 */
const FeatureRow: React.FC<{
  feature: FeatureDiscoveryItem;
}> = ({ feature }) => {
  const Icon = getFeatureIcon(feature.feature);
  const discoveryRate = feature.discoveryRate;

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-stone-50 transition-colors">
      {/* Icon */}
      <div className={clsx(
        'w-10 h-10 rounded-lg flex items-center justify-center',
        discoveryRate >= 70 ? 'bg-green-100 text-green-600' :
        discoveryRate >= 40 ? 'bg-amber-100 text-amber-600' :
        'bg-red-100 text-red-600'
      )}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Feature info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-stone-900">{feature.label}</span>
          <span className="text-sm font-bold text-stone-900">{discoveryRate}%</span>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              discoveryRate >= 70 ? 'bg-green-500' :
              discoveryRate >= 40 ? 'bg-amber-500' : 'bg-red-500'
            )}
            style={{ width: `${discoveryRate}%` }}
          />
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
          <span>{feature.usersDiscovered} discovered</span>
          <span>{feature.usersNotDiscovered} haven't used</span>
          {feature.avgDaysToDiscover > 0 && (
            <span>Avg: {feature.avgDaysToDiscover}d to discover</span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Feature Discovery Card Component
 */
export const FeatureDiscoveryCard: React.FC<FeatureDiscoveryCardProps> = ({
  data,
  isLoading = false,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <DiscoverySkeleton />
      </Card>
    );
  }

  const { features, totalActiveUsers, usersWithAllFeatures, avgFeaturesPerUser } = data;

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Feature Discovery</h3>
          <p className="text-sm text-stone-500">Track which features users are adopting</p>
        </div>
        <div className="flex items-center gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-stone-900">{avgFeaturesPerUser.toFixed(1)}</div>
            <div className="text-xs text-stone-500">Avg features</div>
          </div>
          <div>
            <div className="text-lg font-bold text-green-600">{usersWithAllFeatures}</div>
            <div className="text-xs text-stone-500">Using all</div>
          </div>
        </div>
      </div>

      {/* Feature list */}
      {features.length === 0 ? (
        <div className="text-center py-8 text-stone-500">
          <Sparkles className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No feature discovery data available</p>
        </div>
      ) : (
        <div className="space-y-2">
          {features.map((feature) => (
            <FeatureRow
              key={feature.feature}
              feature={feature}
            />
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-stone-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-stone-600">Active users analyzed</span>
          <span className="font-medium text-stone-900">{totalActiveUsers}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-stone-600">Users using all features</span>
          <span className="font-medium text-green-600">
            {usersWithAllFeatures} ({totalActiveUsers > 0 ? Math.round((usersWithAllFeatures / totalActiveUsers) * 100) : 0}%)
          </span>
        </div>
      </div>
    </Card>
  );
};

export default FeatureDiscoveryCard;
